import { useEffect, useRef, useState } from 'react'

import { useLazyGetWindowUsageQuery } from '../../app/apiSlice'
import { mergeWindowUsageData } from '../../common/utils/windowUsageDataUtils'
import type { WindowUsageData } from '../../features/BusinessApps/types'
import useBusinessAppsFilters from '../../features/BusinessApps/useBusinessAppsFilters'
import { getWeekdayDateRanges } from '../../features/BusinessApps/utils'
import type { TagOfInterestSource, Variables } from './useFilteredWindowUsageQuery'
import useQueryFilters from './useQueryFilters'

interface UseWindowUsageQueryResult {
  dataList: WindowUsageData[] | undefined
  isLoading: boolean
  error: string | null
}

export const useWindowUsageQuery = (
  tagsOfInterestSource: TagOfInterestSource,
  variables?: Variables | undefined,
): UseWindowUsageQueryResult => {
  const { min_date, max_date, team_ids, tags_of_interest } = useQueryFilters()
  const { isIgnoreWeekendsActive } = useBusinessAppsFilters()

  const [dataList, setDataList] = useState<WindowUsageData[] | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [getWindowUsage] = useLazyGetWindowUsageQuery()
  const currentRequestRef = useRef<number>(0)

  useEffect(() => {
    const fetchWindowUsageData = async () => {
      if (!min_date || !max_date) return

      // Increment request counter and store current request ID
      currentRequestRef.current += 1
      const requestId = currentRequestRef.current

      setIsLoading(true)
      setError(null)

      try {
        let windowData: WindowUsageData[] | undefined

        if (!isIgnoreWeekendsActive) {
          // Regular fetch for all days including weekends
          const windowUsageResult = await getWindowUsage({
            body: {
              tags_of_interest: tags_of_interest[tagsOfInterestSource],
              sort_by_field: 'time_h',
              min_date,
              max_date,
              team_ids,
              ...(variables ?? {}),
            },
          })
          windowData = windowUsageResult.data as WindowUsageData[] | undefined
        } else {
          // Fetch for weekdays only
          const dateRanges = getWeekdayDateRanges(min_date, max_date)

          // Get window usage data for all weekday ranges
          const windowUsagePromises = dateRanges.map(({ firstDate, lastDate }) =>
            getWindowUsage({
              body: {
                tags_of_interest: tags_of_interest[tagsOfInterestSource],
                sort_by_field: 'time_h',
                min_date: firstDate,
                max_date: lastDate,
                team_ids,
                ...(variables ?? {}),
              },
            }),
          )

          const windowUsageResults = await Promise.all(windowUsagePromises)

          // Combine all window usage results with proper merging logic
          const resultDataArrays = windowUsageResults.map(
            (result) => result.data as WindowUsageData[] | undefined,
          )
          windowData = mergeWindowUsageData(resultDataArrays)
        }

        // Only update state if this is still the most recent request
        if (requestId === currentRequestRef.current) {
          setDataList(windowData)
        }
      } catch (err) {
        // Only set error if this is still the most recent request
        if (requestId === currentRequestRef.current) {
          const errorMessage =
            err instanceof Error
              ? err.message
              : 'An error occurred while fetching window usage data'
          setError(errorMessage)
          console.error(`[Request #${requestId}] Error fetching window usage data:`, err)
        }
      } finally {
        // Only update loading state if this is still the most recent request
        if (requestId === currentRequestRef.current) {
          setIsLoading(false)
        }
      }
    }

    fetchWindowUsageData()
  }, [
    min_date,
    max_date,
    team_ids,
    tags_of_interest,
    isIgnoreWeekendsActive,
    getWindowUsage,
    tagsOfInterestSource,
    variables,
  ])

  return {
    dataList,
    isLoading,
    error,
  }
}

